import { useCallback, useRef, useState } from 'react';
import { platformApiUrl } from '@/core/config/env';
import { ApiError, currentAccessToken } from '@/core/http/client';
import { Button, cx } from '@/design-system';
import type { FieldComponentProps } from '../field-registry';

/**
 * ENVIO DE FOTOS E VÍDEO
 *
 * O arquivo sobe assim que é escolhido, e o formulário guarda apenas a URL. É
 * o oposto de acumular arquivos e mandar tudo no envio final: quem preenche
 * oito etapas com dez fotos na memória perde tudo se a conexão cair no último
 * clique.
 *
 * O upload passa pela API do acervo, que valida tipo, tamanho e — o que
 * importa — se o CONTEÚDO corresponde ao tipo declarado. `Content-Type` é
 * escrito por quem envia e não prova nada.
 */

type Enviando = { id: string; nome: string; erro: string | null };

/** O token é injetado pelo cliente HTTP; aqui só reaproveitamos a base. */
const enderecoDeUpload = () => `${platformApiUrl ?? '/api/v1'}/me/media`;

async function enviarArquivos(
  arquivos: File[],
  token: string | null,
): Promise<{ url: string; kind: string }[]> {
  const corpo = new FormData();
  for (const arquivo of arquivos) corpo.append('files', arquivo);

  const resposta = await fetch(enderecoDeUpload(), {
    method: 'POST',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: corpo,
  });

  const texto = await resposta.text();
  const dados: unknown = texto ? JSON.parse(texto) : null;

  if (!resposta.ok) {
    const problema = dados as { detail?: string; title?: string } | null;
    throw new ApiError(resposta.status, null, problema?.detail ?? problema?.title ?? 'Falha ao enviar.');
  }

  return dados as { url: string; kind: string }[];
}

/** O mesmo token que o cliente HTTP usa — ele vive em memoria, nao em storage. */
function useToken(): () => string | null {
  return useCallback(() => currentAccessToken(), []);
}

export function GalleryControl({ field, value, onChange, disabled, inputId, error }: FieldComponentProps) {
  const urls = Array.isArray(value) ? (value as string[]) : [];
  const entrada = useRef<HTMLInputElement>(null);
  const lerToken = useToken();

  const [enviando, setEnviando] = useState<Enviando[]>([]);
  const [falha, setFalha] = useState<string | null>(null);

  const limite = field.validation.max ?? null;
  const cheio = limite !== null && urls.length >= limite;

  const escolher = async (lista: FileList | null) => {
    if (!lista || lista.length === 0) return;
    setFalha(null);

    const arquivos = [...lista];

    // Corta no limite ANTES de enviar: subir uma foto que o plano não comporta
    // gasta a banda de quem está no celular para depois recusar.
    const cabem = limite === null ? arquivos : arquivos.slice(0, Math.max(0, limite - urls.length));

    if (cabem.length < arquivos.length) {
      setFalha(`Seu plano permite ${limite} foto(s). As demais foram ignoradas.`);
    }

    if (cabem.length === 0) return;

    const pendentes = cabem.map((arquivo) => ({
      id: `${arquivo.name}-${arquivo.size}-${Math.random()}`,
      nome: arquivo.name,
      erro: null,
    }));

    setEnviando((atual) => [...atual, ...pendentes]);

    try {
      const salvos = await enviarArquivos(cabem, lerToken());
      onChange([...urls, ...salvos.map((item) => item.url)]);
    } catch (erro) {
      setFalha(erro instanceof Error ? erro.message : 'Não foi possível enviar.');
    } finally {
      setEnviando((atual) => atual.filter((item) => !pendentes.some((p) => p.id === item.id)));
      if (entrada.current) entrada.current.value = '';
    }
  };

  const remover = (url: string) => onChange(urls.filter((item) => item !== url));

  /** Troca de posição com o vizinho. Previsível, e funciona no teclado. */
  const mover = (indice: number, direcao: -1 | 1) => {
    const destino = indice + direcao;
    if (destino < 0 || destino >= urls.length) return;

    const copia = [...urls];
    const [movido] = copia.splice(indice, 1);
    copia.splice(destino, 0, movido!);
    onChange(copia);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={entrada}
          id={inputId}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          disabled={disabled || cheio}
          onChange={(evento) => void escolher(evento.target.files)}
        />

        <Button
          type="button"
          variant="outline"
          disabled={disabled || cheio}
          onClick={() => entrada.current?.click()}
        >
          {urls.length === 0 ? 'Escolher fotos' : 'Adicionar mais'}
        </Button>

        <span className="text-xs text-ink-soft">
          {limite === null
            ? `${urls.length} foto(s)`
            : `${urls.length} de ${limite} foto(s) do seu plano`}
        </span>
      </div>

      {enviando.length > 0 ? (
        <p className="text-sm text-ink-soft" role="status">
          Enviando {enviando.length} arquivo(s)…
        </p>
      ) : null}

      {falha ?? error ? <p className="text-sm text-danger">{falha ?? error}</p> : null}

      {urls.length > 0 ? (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {urls.map((url, indice) => (
            <li
              key={url}
              className={cx(
                'group relative overflow-hidden rounded-(--radius-card) border bg-surface',
                indice === 0 ? 'border-primary' : 'border-line',
              )}
            >
              <img src={url} alt={`Foto ${indice + 1}`} className="h-28 w-full object-cover" loading="lazy" />

              {/* A primeira é a capa. Dizer isso evita a dúvida de qual aparece na busca. */}
              {indice === 0 ? (
                <span className="absolute left-1 top-1 rounded-full bg-primary px-2 py-0.5 text-[0.625rem] font-bold uppercase text-primary-fg">
                  Capa
                </span>
              ) : null}

              <div className="flex items-center justify-between gap-1 border-t border-line p-1">
                <div className="flex gap-0.5">
                  <button
                    type="button"
                    aria-label={`Mover a foto ${indice + 1} para a esquerda`}
                    disabled={indice === 0 || disabled}
                    onClick={() => mover(indice, -1)}
                    className="rounded px-1.5 py-0.5 text-sm text-ink-soft hover:bg-surface-muted disabled:opacity-40"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    aria-label={`Mover a foto ${indice + 1} para a direita`}
                    disabled={indice === urls.length - 1 || disabled}
                    onClick={() => mover(indice, 1)}
                    className="rounded px-1.5 py-0.5 text-sm text-ink-soft hover:bg-surface-muted disabled:opacity-40"
                  >
                    →
                  </button>
                </div>

                <button
                  type="button"
                  aria-label={`Remover a foto ${indice + 1}`}
                  disabled={disabled}
                  onClick={() => remover(url)}
                  className="rounded px-1.5 py-0.5 text-xs text-danger hover:bg-danger-soft"
                >
                  Remover
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function VideoControl({ field, value, onChange, disabled, inputId, error }: FieldComponentProps) {
  const url = typeof value === 'string' && value.length > 0 ? value : null;
  const entrada = useRef<HTMLInputElement>(null);
  const lerToken = useToken();

  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);

  const escolher = async (lista: FileList | null) => {
    const arquivo = lista?.[0];
    if (!arquivo) return;

    setFalha(null);
    setEnviando(true);

    try {
      const [salvo] = await enviarArquivos([arquivo], lerToken());
      if (salvo) onChange(salvo.url);
    } catch (erro) {
      setFalha(erro instanceof Error ? erro.message : 'Não foi possível enviar.');
    } finally {
      setEnviando(false);
      if (entrada.current) entrada.current.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <input
        ref={entrada}
        id={inputId}
        type="file"
        accept="video/mp4,video/webm"
        className="hidden"
        disabled={disabled}
        onChange={(evento) => void escolher(evento.target.files)}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" disabled={disabled || enviando} onClick={() => entrada.current?.click()}>
          {url ? 'Trocar vídeo' : 'Escolher vídeo'}
        </Button>

        {url ? (
          <Button type="button" variant="ghost" disabled={disabled} onClick={() => onChange(null)}>
            Remover
          </Button>
        ) : null}

        <span className="text-xs text-ink-soft">MP4 ou WEBM, até 80 MB.</span>
      </div>

      {enviando ? <p className="text-sm text-ink-soft" role="status">Enviando o vídeo…</p> : null}
      {falha ?? error ? <p className="text-sm text-danger">{falha ?? error}</p> : null}

      {url ? (
        <video src={url} controls preload="metadata" className="w-full max-w-lg rounded-(--radius-card) border border-line">
          <track kind="captions" />
        </video>
      ) : null}

      {field.presentation.help ? <p className="text-xs text-ink-faint">{field.presentation.help}</p> : null}
    </div>
  );
}
